import type { AIService } from './AIService'
import type { SarahAskInput, SarahReply, PracticeScenario, SarahContext } from '@/domain/sarah'
import { findConceptByPrompt } from '@/data/seed/sarahConcepts'
import { sarahScenarios, getScenarioByTopic } from '@/data/seed/sarahScenarios'

const DEFAULT_FOLLOW_UPS = ['Give me a phishing scenario', 'Test me', 'What should I review next?']

const PRACTICE_KEYWORDS = ['test me', 'quiz me', 'practice', 'scenario', 'give me a']
const MISTAKE_KEYWORDS = ['mistake', 'wrong', 'why was i wrong', 'get wrong', 'missed']
const REVIEW_KEYWORDS = ['what should i review', 'review next', 'recommend', 'what next']
const GREETING_KEYWORDS = ['hello', 'hi ', 'hi,', 'hey', "what's up", 'good morning', 'good afternoon']

// Requests for genuinely offensive/malicious cyber activity — Sarah redirects
// these toward defensive education instead of answering them (P3 AI safety
// requirement). Deliberately narrow and keyword-based for dev mode; a
// production model would need real content-safety classification.
const HARMFUL_KEYWORDS = [
  'hack into',
  'hack someone',
  "hack my ex",
  'write malware',
  'write a virus',
  'create a virus',
  'ddos',
  'steal a password',
  'steal someone',
  "crack someone's password",
  'bypass security',
  'keylogger',
  'spy on',
  'break into an account',
]

// Off-topic requests Sarah should not attempt — she's a cybersecurity coach,
// not a general-purpose assistant (P3 product scope).
const OFF_TOPIC_KEYWORDS = [
  'legal advice',
  'medical advice',
  'diagnos',
  'prescription',
  'stock tip',
  'investment advice',
  'should i invest',
  'write my essay',
  'homework',
]

function pickRandomScenario(): PracticeScenario {
  return sarahScenarios[Math.floor(Math.random() * sarahScenarios.length)]
}

function greetingLine(context: SarahContext): string {
  return `Hi ${context.learnerFirstName}, I'm Sarah — your CyberTales Learning coach. I can explain a concept, walk through something you got wrong, or run you through a quick practice scenario. What would help right now?`
}

// Zero-cost implementation of AIService (Document 11 §4/P3). Deterministic
// and entirely local — no network call, no API key. Every response is
// built from a small, curated cybersecurity knowledge base plus whatever
// SarahContext the calling page assembled; nothing here queries a
// repository or recomputes P1/P2 aggregates itself (that data arrives
// already-computed in `context`, per the P3 architecture requirement).
// CloudSecureAIService (P5) implements this same interface against Azure
// OpenAI — the Sarah UI is not aware which one it's talking to.
export class DevAIService implements AIService {
  async ask({ prompt, context }: SarahAskInput): Promise<SarahReply> {
    const lower = prompt.toLowerCase().trim()

    if (HARMFUL_KEYWORDS.some((k) => lower.includes(k))) {
      return {
        message:
          "I can't help with that — my role is defensive cybersecurity education, not offensive techniques. If you're worried about an account or device, I'm glad to walk through how to secure it, spot the warning signs, or report it properly.",
        followUpPrompts: ['How do I secure my account?', 'Give me a phishing scenario', 'Explain phishing'],
      }
    }

    if (OFF_TOPIC_KEYWORDS.some((k) => lower.includes(k))) {
      return {
        message:
          "That's outside what I can help with — I'm focused on cybersecurity awareness, not legal, medical, or financial advice. Happy to help with anything cyber-safety related though.",
        followUpPrompts: DEFAULT_FOLLOW_UPS,
      }
    }

    if (GREETING_KEYWORDS.some((k) => lower.includes(k)) && lower.length < 40) {
      return { message: greetingLine(context), followUpPrompts: DEFAULT_FOLLOW_UPS }
    }

    if (PRACTICE_KEYWORDS.some((k) => lower.includes(k))) {
      return this.requestScenario(undefined, context)
    }

    if (MISTAKE_KEYWORDS.some((k) => lower.includes(k))) {
      return this.explainMistake(context)
    }

    if (REVIEW_KEYWORDS.some((k) => lower.includes(k))) {
      return this.recommendReview(context)
    }

    const concept = findConceptByPrompt(lower)
    if (concept) {
      return {
        message: `${concept.explanation}\n\nThis is exactly what "${concept.relatedEpisodeTitle}" covers — worth a rewatch if you want the full story.`,
        followUpPrompts: [`Give me a ${concept.topic} scenario`, 'Test me', 'What should I review next?'],
      }
    }

    return {
      message: context.episodeTitle
        ? `I'm not sure I follow that one — but if you want, I can explain a concept from "${context.episodeTitle}", walk through a mistake, or give you a quick scenario to practice on.`
        : "I'm not sure I follow that one — I'm best at explaining cybersecurity concepts, reviewing quiz mistakes, and running short practice scenarios. What would help?",
      followUpPrompts: DEFAULT_FOLLOW_UPS,
    }
  }

  async answerScenario(scenario: PracticeScenario, selectedOptionId: string, _context: SarahContext): Promise<SarahReply> {
    const correct = selectedOptionId === scenario.correctOptionId
    return {
      message: correct
        ? `✓ ${scenario.correctFeedback}`
        : `${scenario.incorrectFeedback}\n\nWant to try another one on the same topic, or move on to something else?`,
      followUpPrompts: correct
        ? ['Give me another scenario', `Explain ${scenario.topic}`, 'What should I review next?']
        : ['Try another scenario', `Explain ${scenario.topic}`, 'What should I review next?'],
    }
  }

  async requestScenario(topic: string | undefined, context: SarahContext): Promise<SarahReply> {
    const resolvedTopic = topic ?? context.episodeTopics?.[0] ?? context.missedTopics?.[0]
    const scenario = (resolvedTopic ? getScenarioByTopic(resolvedTopic) : null) ?? pickRandomScenario()

    return {
      message: `Let's practice ${scenario.topic.replace('-', ' ')} detection.\n\n${scenario.setup}`,
      followUpPrompts: [],
      scenario,
    }
  }

  private explainMistake(context: SarahContext): SarahReply {
    if (!context.missedTopics || context.missedTopics.length === 0) {
      return {
        message: context.quizScorePercent !== undefined
          ? `You scored ${context.quizScorePercent}% — nice work overall. I don't have details on a specific question you missed right now, but I'm glad to explain any concept or give you a scenario to sharpen up on.`
          : "I don't have a specific quiz result in front of me right now — open me from a quiz result page and I can walk through exactly what you missed.",
        followUpPrompts: DEFAULT_FOLLOW_UPS,
      }
    }
    const topic = context.missedTopics[0]
    const concept = findConceptByPrompt(topic)
    const explanation = concept
      ? concept.explanation
      : `The question touched on "${topic}" — worth reviewing the episode again if it's not clicking yet.`
    return {
      message: `${explanation}\n\nWant to try a quick practice scenario on this so it sticks?`,
      followUpPrompts: [`Give me a ${concept?.topic ?? 'related'} scenario`, 'What should I review next?'],
    }
  }

  private recommendReview(context: SarahContext): SarahReply {
    if (context.weakestOrgTopic) {
      return {
        message: `Across your organization, "${context.weakestOrgTopic.episodeTitle}" has the lowest quiz pass rate right now (${context.weakestOrgTopic.passRatePercent}%). That's a good one to revisit, or practice with a scenario here.`,
        followUpPrompts: ['Give me a scenario on that', 'Explain this topic', 'Test me'],
      }
    }
    if (context.missedTopics && context.missedTopics.length > 0) {
      return {
        message: `Based on your last quiz, I'd revisit "${context.missedTopics[0]}" — that's where you lost the most points.`,
        followUpPrompts: ['Explain this topic', 'Give me a scenario on that', 'Test me'],
      }
    }
    if (context.completedEpisodeCount !== undefined && context.totalEpisodeCount !== undefined && context.completedEpisodeCount < context.totalEpisodeCount) {
      return {
        message: `You've completed ${context.completedEpisodeCount} of ${context.totalEpisodeCount} episodes so far — the Episode Library has what's left. Once you've watched one, come back and I'll help you practice it.`,
        followUpPrompts: ['Test me on what I know', 'Explain phishing', 'Explain passwords'],
      }
    }
    return {
      message: "You're caught up on everything I can see right now — nice work. Want a scenario to keep your skills sharp?",
      followUpPrompts: DEFAULT_FOLLOW_UPS,
    }
  }
}
